import { startDashboardActivity } from "./dashboard-activity";
import { PrivacyChoicesButton } from "@withmethod/design/privacy";
import { AgentCopyButton } from "../../../packages/design/src/AgentCopyButton";
import { authoringPrompt } from "@method-ai-hq/sdk/authoring-prompt";
import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Link,
  Outlet,
  useLocation,
  useMatches,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog } from "@base-ui/react/dialog";
import { Menu } from "@base-ui/react/menu";
import {
  CaretDownIcon,
  CircleNotchIcon,
  GitBranchIcon,
  ListIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  PlusIcon,
  SidebarSimpleIcon,
  SignOutIcon,
  SlidersHorizontalIcon,
  XIcon,
  TrayIcon,
  ScalesIcon,
} from "@phosphor-icons/react";
import { api, UnauthorizedError, type WorkflowSummary } from "./api";
import { ViewingBanner } from "./StaffPage";
import type { Inbox } from "../../../packages/contracts/src/dashboard";
import { SignedOutScreen } from "./Login";
import { consumeCreateIntent } from "./create-intent";
import { EXAMPLE_PROMPT } from "./example-prompt";
import { ApiKeys } from "./ApiKeys";
import { Members } from "./Members";
import { OtherWorkspaceBanner, WorkspaceMenu } from "./WorkspaceMenu";
import { bootRequest } from "./boot-requests";
import { Button, buttonVariants, cn } from "./components/ui/button";
import {
  DashboardLayout,
  DashboardSidebar,
  DashboardMain,
  DashboardPanel,
} from "./components/ui/dashboard-layout";

import type { SavedRun } from "../../../packages/contracts/src/workspace";
import { MethodNavigation } from "./MethodNavigation";
import { SearchDialog } from "./components/dashboard/SearchDialog";

import { LoadingStarted, LoadingLines, PageLoading, ReaderLoading } from "./Loading";
import { WorkspaceScope, runListRefresh, runQueryOptions } from "./workspace-queries";

// Method and run pages load their editor code only when they open.
const WorkflowView = lazy(() =>
  import("./App").then((module) => ({ default: module.WorkflowView })),
);
export type DashboardPage =
  | "runs"
  | "run"
  | "inbox"
  | "comparisons"
  | "comparison"
  | "workflows"
  | "settings"
  | "detail"
  | "new"
  | "staff"
  | "not-found";
type DashboardContextValue = {
  workflows: WorkflowSummary[];
  workflowsLoading: boolean;
  workflowsError?: string;
  accountScope: string;
  runs: SavedRun[];
  runsLoading: boolean;
  runsError?: string;
  /** The open Inbox items. The sidebar count and the Methods panel's "Needs you" read it. */
  inbox?: Inbox;
  /** Save the person's pinned Methods in this order. */
  setPins: (ids: string[]) => Promise<void>;
  setRename: (workflow: WorkflowSummary) => void;
  refresh: () => Promise<void>;
  updateWorkflow: (workflow: WorkflowSummary) => void;
  signOut: () => Promise<void>;
  /** This person may use God Mode. */
  staff: boolean;
};
const DashboardContext = createContext<DashboardContextValue | null>(null);
export function useDashboard() {
  const context = useContext(DashboardContext);
  if (!context) throw new Error("This page requires the dashboard layout.");
  return context;
}
export function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const params = useParams({ strict: false });
  const searchParams = useSearch({ strict: false });
  const page =
    useMatches({
      select: (matches) =>
        [...matches].reverse().find((match) => match.staticData.page)
          ?.staticData.page,
    }) ?? "not-found";
  const loadingStarted = useMemo(() => Date.now(), [location.pathname, searchParams.version, searchParams.compare]);
  const [signedOut, setSignedOut] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [rename, setRename] = useState<WorkflowSummary | null>(null);
  const [actionError, setActionError] = useState("");
  const sessionQuery = useQuery({
    queryKey: ["session"],
    queryFn: () => bootRequest("session", api.me),
    enabled: !signedOut,
  });
  const session = sessionQuery.data;
  useEffect(() => {
    // A staff member's view is not the account's own activity.
    if (!session || signedOut || session.viewing_as) return;
    return startDashboardActivity(() => api.recordDashboardActivity());
  }, [session?.user.id, session?.viewing_as, signedOut]);
  const scope = [session?.organization.id, session?.user.id];
  const accountScope = `${session?.organization.id ?? ""}:${session?.user.id ?? ""}`;
  const workflowKey = ["workflows", ...scope];
  const runQuery = useQuery({
    queryKey: ["runs", "overview", accountScope], queryFn: () => bootRequest("runs:overview", api.runOverview),
    enabled: !!session && !signedOut, refetchInterval: runListRefresh,
  });
  const selectedRunQuery = useQuery({
    ...runQueryOptions(accountScope, params.runId),
    enabled: !!session && !!params.runId && !signedOut,
  });
  const selectedRun = selectedRunQuery.data ?? runQuery.data?.find(run => run.id === params.runId)
    ?? queryClient.getQueryData<SavedRun[]>(["runs", "list", accountScope])?.find(run => run.id === params.runId);
  const selectedMethod = params.workflowId ?? selectedRun?.workflow_id;
  const workflowQuery = useQuery({
    queryKey: workflowKey,
    queryFn: () => bootRequest("workflows", api.list),
    enabled: !!session && !signedOut,

  });
  const inboxQuery = useQuery({
    queryKey: ["inbox"], queryFn: () => api.inbox(),
    enabled: !!session && !signedOut, refetchInterval: 30_000,
  });
  const workflows = workflowQuery.data ?? [];
  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["workflows"] }),
      queryClient.invalidateQueries({ queryKey: ["runs"] }),
      queryClient.invalidateQueries({ queryKey: ["inbox"] }),
    ]);
    setActionError("");
  }, [queryClient]);
  const setPins = useCallback(async (ids: string[]) => {
    const key = ["workflows", session?.organization.id, session?.user.id];
    queryClient.setQueryData<WorkflowSummary[]>(key, current => current?.map(w => ({ ...w, pinned_position: ids.includes(w.id) ? ids.indexOf(w.id) : null })));
    try { await api.setPins(ids); setActionError(""); }
    catch {
      setActionError("Could not save your pinned Methods. Please try again.");
      await queryClient.invalidateQueries({ queryKey: ["workflows"] });
    }
  }, [queryClient, session?.organization.id, session?.user.id]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    const onUnauthorized = () => {
      setSignedOut(true);
      queryClient.clear();
    };
    window.addEventListener("workflow:unauthorized", onUnauthorized);
    return () =>
      window.removeEventListener("workflow:unauthorized", onUnauthorized);
  }, [queryClient]);
  useEffect(() => {
    if (!session) return;
    const intent = consumeCreateIntent(window.sessionStorage);
    if (intent && location.pathname === "/")
      void navigate({
        to: "/new",
        replace: true,
      });
  }, [session, navigate]);
  useEffect(() => {
    setMobileOpen(false);
  }, [location.href]);
  const updateWorkflow = useCallback(
    (value: WorkflowSummary) => {
      queryClient.setQueryData<WorkflowSummary[]>(
        ["workflows", session?.organization.id, session?.user.id],
        // A detail response is not a complete method list. Do not let an early
        // response erase the saved sidebar order before the list arrives.
        (current) => current === undefined ? current :
          current.some((w) => w.id === value.id)
            ? current.map((w) => (w.id === value.id ? { ...w, ...value } : w))
            : [...current, value],
      );
    },
    [queryClient, session?.organization.id, session?.user.id],
  );
  const signOut = async () => {
    try {
      const url = await api.logout();
      queryClient.clear();
      window.location.assign(url);
    } catch {
      setActionError("Sign out failed. Please try again.");
    }
  };
  if (signedOut || sessionQuery.error instanceof UnauthorizedError)
    return <SignedOutScreen />;
  if (!session && !sessionQuery.isPending)
    return (
      <div className="dash-state" role="alert">
        <p>{sessionQuery.error?.message || "Your account could not load."}</p>
        <Button onClick={() => void sessionQuery.refetch()}>Try again</Button>
      </div>
    );
  const name = session?.user.name || session?.user.email || "Your account";
  const title = page === "new"
    ? "New method"
    : page === "runs" || page === "run" ? "Runs" : page === "inbox" ? "Inbox" : page === "comparisons" || page === "comparison" ? "Comparisons" : page === "detail"
      ? workflows.find((w) => w.id === params.workflowId)?.name || "Method"
      : page === "settings"
        ? "Account"
        : page === "staff" ? "God Mode"
        : page === "not-found"
          ? "Page not found"
          : "Methods";
  const error =
    actionError || workflowQuery.error?.message;
  const sidebar = (
    <>
      <div className="dash-workspace">
        <WorkspaceMenu current={session?.organization.name} />
        <Button
          variant="ghost"
          size="icon"
          className="sidebar-collapse max-[640px]:hidden"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          onClick={() => setCollapsed(!collapsed)}
        >
          <SidebarSimpleIcon size={17} />
        </Button>
      </div>
      <div className="sidebar-divider" />
      <button type="button" className="sidebar-search" title="Search (⌘K)" aria-keyshortcuts="Meta+K Control+K" onClick={() => setSearchOpen(true)}>
        <MagnifyingGlassIcon size={16} />
        <span>Search</span>
        <kbd className="sb-kbd">⌘K</kbd>
      </button>
      <Link
        className={cn(buttonVariants(), "sidebar-new w-full justify-start")}
        to="/new"
        title="New method"
      >
        <PlusIcon size={17} />
        <span>New method</span>
      </Link>
      <nav className="sidebar-nav" aria-label="Main navigation">
        <Link to="/inbox" className={page === "inbox" ? "selected" : ""} aria-current={page === "inbox" ? "page" : undefined} title="Inbox">
          <TrayIcon size={17} /><span>Inbox</span>
          {!!inboxQuery.data?.count && <small className="attention-count" aria-label={`${inboxQuery.data.count} open`}>{inboxQuery.data.count}</small>}
        </Link>
        <Link to="/methods" className={page === "workflows" || page === "detail" ? "selected" : ""}
          aria-current={page === "workflows" || page === "detail" ? "page" : undefined} title="Methods">
          <GitBranchIcon size={17} /><span>Methods</span>
        </Link>
        <Link to="/runs" search={{}} className={page === "runs" || page === "run" ? "selected" : ""} aria-current={page === "runs" || page === "run" ? "page" : undefined} title="Runs"><ListIcon size={17}/><span>Runs</span></Link>
        <Link to="/comparisons" className={page === "comparisons" || page === "comparison" ? "selected" : ""} aria-current={page === "comparisons" || page === "comparison" ? "page" : undefined} title="Comparisons"><ScalesIcon size={17}/><span>Comparisons</span></Link>
      </nav>
      <div className="sidebar-divider" />
      <MethodNavigation workflows={workflows} selectedMethod={selectedMethod} onPins={ids => void setPins(ids)}
        loading={!session || workflowQuery.isPending} error={workflowQuery.error?.message} />
      <div className="sidebar-account">
        {!session ? <LoadingLines className="sidebar-account-loading" rows={1} label="Loading account" /> : <Menu.Root>
          <Menu.Trigger className="account-trigger">
            <span className="account-avatar">
              {name.slice(0, 1).toUpperCase()}
            </span>
            <span>{name}</span>
            <CaretDownIcon size={12} />
          </Menu.Trigger>
          <Menu.Portal>
            <Menu.Positioner side="top" align="start" sideOffset={8}>
              <Menu.Popup className="dash-menu">
                <Menu.Item onClick={() => void navigate({ to: "/settings" })}>
                  <SlidersHorizontalIcon size={15} />
                  Account
                </Menu.Item>
                {session.staff && <Menu.Item onClick={() => void navigate({ to: "/staff" })}>
                  <MagnifyingGlassIcon size={15} />
                  God Mode
                </Menu.Item>}
                <Menu.Item onClick={() => void signOut()}>
                  <SignOutIcon size={15} />
                  Sign out
                </Menu.Item>
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>}
      </div>
    </>
  );
  return (
    <WorkspaceScope.Provider value={accountScope}><LoadingStarted.Provider value={loadingStarted}><DashboardContext.Provider
      value={{
        workflows,
        accountScope,
        workflowsLoading: workflowQuery.isPending,
        workflowsError: workflowQuery.error?.message,
        runs: runQuery.data ?? [],
        runsLoading: runQuery.isPending,
        runsError: runQuery.error?.message,
        inbox: inboxQuery.data,
        setPins,
        setRename,
        refresh,
        updateWorkflow,
        signOut,
        staff: !!session?.staff,
      }}
    >
      <DashboardLayout className={collapsed ? "sidebar-is-collapsed" : ""}>
        <DashboardSidebar collapsed={collapsed}>{sidebar}</DashboardSidebar>
        <Dialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
          <Dialog.Portal>
            <Dialog.Backdrop className="dash-backdrop" />
            <Dialog.Popup className="mobile-sidebar">
              <Dialog.Title className="visually-hidden">
                Navigation
              </Dialog.Title>
              <Dialog.Close
                className="dash-icon-button mobile-close"
                aria-label="Close navigation"
              >
                <XIcon size={18} />
              </Dialog.Close>
              {sidebar}
            </Dialog.Popup>
          </Dialog.Portal>
        </Dialog.Root>
        <DashboardMain>
          {session?.viewing_as && <ViewingBanner name={session.user.name} email={session.user.email} />}
          {!session?.viewing_as && <OtherWorkspaceBanner />}
          <header className="dashboard-toolbar">
            <Button
              variant="ghost"
              size="icon"
              className="mobile-menu hidden max-[640px]:inline-flex"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
            >
              <ListIcon size={20} />
            </Button>
            {page !== "detail" && <span className="toolbar-title">{title}</span>}
            {page === "workflows" ? (
              <>
                <Link
                  className={cn(buttonVariants(), "toolbar-new ml-auto")}
                  to="/new"
                >
                  <PlusIcon size={15} />
                  New method
                </Link>
              </>
            ) : null}
          </header>
          <DashboardPanel
            className={
              (page === "detail" || page === "run") ? "panel-document" : ""
            }
          >
            {error && (
              <div className="dash-notice" role="alert">
                {error}
                <Button variant="ghost" onClick={() => void refresh()}>
                  Retry
                </Button>
              </div>
            )}
            {session ? <Outlet /> : <PageLoading page={page} />}
          </DashboardPanel>
        </DashboardMain>
        <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
        <RenameDialog
          workflow={rename}
          onClose={() => setRename(null)}
          onSaved={refresh}
        />
      </DashboardLayout>
    </DashboardContext.Provider></LoadingStarted.Provider></WorkspaceScope.Provider>
  );
}
/** A new Method is built by the person's coding agent. The page opens the Method when it is saved. */
export function NewWorkflowPage() {
  const { workflows, workflowsLoading } = useDashboard();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [known, setKnown] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (!known && !workflowsLoading) setKnown(new Set(workflows.map((w) => w.id)));
  }, [known, workflows, workflowsLoading]);
  useEffect(() => {
    const timer = setInterval(() => void queryClient.invalidateQueries({ queryKey: ["workflows"] }), 5000);
    return () => clearInterval(timer);
  }, [queryClient]);
  const created = known && workflows.find((w) => !known.has(w.id));
  useEffect(() => {
    if (created) void navigate({ to: "/methods/$workflowId", params: { workflowId: created.id } });
  }, [created, navigate]);
  return (
    <div className="dash-state methods-empty">
      <GitBranchIcon size={27} />
      <h2>Turn repeated work into a Method</h2>
      <p>Paste these instructions into Codex, Claude Code, or Cursor. Your agent builds the Method, runs it, and saves it here.</p>
      <AgentCopyButton text={authoringPrompt} label="Copy instructions to agent" />
      <div className="new-method-example">
        <AgentCopyButton text={`${authoringPrompt}\n\nUse this work for the first Method:\n\n${EXAMPLE_PROMPT}`} label="Or copy a ready example" />
      </div>
      <p className="new-method-status" role="status"><CircleNotchIcon className="spin" size={14} /> Waiting for your agent to save the Method…</p>
    </div>
  );
}
export function WorkflowDetailPage() {
  const { workflowId } = useParams({ strict: false });
  const { updateWorkflow } = useDashboard();
  const navigate = useNavigate();
  return (
    <Suspense fallback={<ReaderLoading />}>
      <WorkflowView
        key={workflowId}
        id={workflowId!}
        onWorkflowChange={updateWorkflow}
      />
    </Suspense>
  );
}
export function AccountPage() {
  const { signOut } = useDashboard();
  const queryClient = useQueryClient();
  const session = useQuery({ queryKey: ["session"], queryFn: api.me }).data;
  const [saving, setSaving] = useState(false), [settingError, setSettingError] = useState<string>();
  const keepOnDevices = async (keep: boolean) => {
    setSaving(true); setSettingError(undefined);
    try { await api.setKeepRunContentOnDevices(keep); await queryClient.invalidateQueries({ queryKey: ["session"] }); }
    catch (error) { setSettingError((error as Error).message || "Could not save the setting. Please try again."); }
    finally { setSaving(false); }
  };
  return (
    <section className="account-page">
      <h1>Account</h1>
      <dl>
        <div>
          <dt>Name</dt>
          <dd>{session?.user.name}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>{session?.user.email}</dd>
        </div>
        <div>
          <dt>Workspace</dt>
          <dd>{session?.organization.name}</dd>
        </div>
      </dl>
      <Members workspace={session?.organization.name} />
      <BillingSection />
      <ApiKeys />
      <h2>Run content</h2>
      <label className="account-setting">
        <input
          type="checkbox"
          checked={!!session?.organization.keep_run_content_on_devices}
          disabled={!session || saving}
          onChange={(e) => void keepOnDevices(e.target.checked)}
        />
        <span>
          <strong>Keep run content on devices</strong>
          <small>For everyone in {session?.organization.name ?? "this workspace"}: runs send only their steps, status, and
            timing to Method. Inputs, outputs, prompts, and files stay on the computer that ran them. Model and
            classify steps still send their inputs to the model provider.</small>
        </span>
      </label>
      {settingError && <p role="alert" className="account-setting-error">{settingError}</p>}
      <h2>Classification data</h2>
      <p>Classification sends the step's declared inputs to Method and its classification
        provider. Method currently covers the cost within service limits. Saved runs
        contain the declared inputs and results.</p>
      <p><PrivacyChoicesButton /></p>
      <Button variant="secondary" onClick={() => void signOut()}>
        <SignOutIcon size={16} />
        Sign out
      </Button>
    </section>
  );
}
const usd = (value: number) => `$${value.toFixed(value % 1 ? 2 : 0)}`;
function BillingSection() {
  const billing = useQuery({ queryKey: ["billing"], queryFn: api.billing });
  const [opening, setOpening] = useState<"checkout" | "portal">(), [error, setError] = useState<string>();
  const returned = new URLSearchParams(window.location.search).get("billing");
  const open = async (target: "checkout" | "portal") => {
    setOpening(target); setError(undefined);
    try { window.location.assign(target === "checkout" ? await api.billingCheckout() : await api.billingPortal()); }
    catch (reason) { setError((reason as Error).message || "Billing could not open. Please try again."); setOpening(undefined); }
  };
  const data = billing.data;
  if (billing.isError) return <><h2>Billing</h2><p role="alert" className="account-setting-error">Billing could not load.</p></>;
  if (!data) return <><h2>Billing</h2><LoadingLines /></>;
  const startup = data.plan === "startup", model = data.hosted_model;
  const ends = data.current_period_end ? new Date(data.current_period_end).toLocaleDateString() : null;
  return (
    <>
      <h2>Billing</h2>
      {returned === "success" && <p role="status">Thank you. Your plan changes to Startup when Stripe confirms the payment.</p>}
      <dl>
        <div>
          <dt>Plan</dt>
          <dd>
            {!data.enabled ? "All features (billing is not set up on this server)" : startup ? "Startup — Self-improving agents, $500/month" : "Free"}
            {data.status === "past_due" && <small className="account-setting-error"> Payment failed. Update your card{ends ? ` before ${ends}` : ""}.</small>}
            {startup && data.status !== "past_due" && ends && <small> Renews or ends {ends}.</small>}
          </dd>
        </div>
        <div>
          <dt>Hosted models</dt>
          <dd>{model.reset === "monthly"
            ? `${model.used_usd === null ? "Usage unavailable" : `${usd(model.used_usd)} of ${usd(model.limit_usd)} used this month`} (fair use; it resets each month).`
            : `${model.used_usd === null ? "Usage unavailable" : `${usd(model.used_usd)} of ${usd(model.limit_usd)} used`}; then use your own key.`}</dd>
        </div>
        <div>
          <dt>Included</dt>
          <dd>{startup
            ? `Production runs, Method Cloud.`
            : `The one-time hosted model credit, then your own key. No production runs.`}</dd>
        </div>
      </dl>
      {data.enabled && !startup && <p><small>Startup adds hosted model fair use each month, production runs,
        Method Cloud, and higher limits.</small></p>}
      {data.enabled && <p className="account-billing-actions">
        {!startup && <Button disabled={!!opening} onClick={() => void open("checkout")}>{opening === "checkout" ? "Opening…" : "Upgrade to Startup — $500/month"}</Button>}
        {data.has_customer && <Button variant="secondary" disabled={!!opening} onClick={() => void open("portal")}>{opening === "portal" ? "Opening…" : "Manage billing"}</Button>}
      </p>}
      {error && <p role="alert" className="account-setting-error">{error}</p>}
    </>
  );
}
function RenameDialog({
  workflow,
  onClose,
  onSaved,
}: {
  workflow: WorkflowSummary | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setName(workflow?.name || "");
    setError("");
  }, [workflow]);
  return (
    <Dialog.Root
      open={!!workflow}
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="dash-backdrop" />
        <Dialog.Popup className="dash-dialog">
          <Dialog.Title>Rename method</Dialog.Title>
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              if (!workflow || !name.trim()) return;
              setSaving(true);
              try {
                await api.rename(workflow.id, name.trim());
                await onSaved();
                onClose();
              } catch (reason) {
                setError(
                  reason instanceof Error
                    ? reason.message
                    : "Could not save the name.",
                );
              } finally {
                setSaving(false);
              }
            }}
          >
            <label htmlFor="workflow-name">Name</label>
            <input
              id="workflow-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={100}
              autoFocus
            />
            {error && <p role="alert">{error}</p>}
            <div className="dialog-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !name.trim()}>
                {saving ? "Saving…" : "Save name"}
              </Button>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
